/**
 * The drag-and-drop source/destination boxes + generate + download flow
 * (ACTION_PLAN.md E4 §1, extended by E5 §3). Flow A (schema-native) and
 * Flow B (sample-data inference) both go through the same drop boxes
 * and the same /generate call — generate/handler.py parses or infers
 * each side itself based on the chosen format.
 */

import { useState } from 'react';

import {
  ApiError,
  generateEtl,
  type CanonicalSchemaShape,
  type GeneratedCode,
} from '../api/client';
import { DeliverAsPr } from '../components/DeliverAsPr';
import { SchemaDropBox, type DroppedSchema } from '../components/DragDropBoxes';
import { GenerationTriangle } from '../components/GenerationTriangle';
import { downloadBlob } from '../lib/download';
import { validationNotes } from '../lib/validationNotes';
import { FieldMapping } from './FieldMapping';
import { SchemaReview } from './SchemaReview';

function formatPence(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

interface GenerateArgs {
  fieldMappingOverrides?: Record<string, string>;
  confirmedSource?: CanonicalSchemaShape;
  confirmedDestination?: CanonicalSchemaShape;
  reviewInferredSchema?: boolean;
}

export function GenerateJob() {
  const [source, setSource] = useState<DroppedSchema | null>(null);
  const [destination, setDestination] = useState<DroppedSchema | null>(null);
  const [reviewInferredSchema, setReviewInferredSchema] = useState(false);
  // E9 — read directly from state inside generate() below rather than
  // threaded through GenerateArgs like reviewInferredSchema is: this
  // choice should carry through every call (the initial generate, the
  // post-review confirm, the post-field-mapping regenerate), whereas
  // reviewInferredSchema deliberately must never leak into those
  // follow-up calls (it means "don't generate yet, return the review").
  const [includeTests, setIncludeTests] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<GeneratedCode | null>(null);
  const [review, setReview] = useState<{
    source: CanonicalSchemaShape;
    destination: CanonicalSchemaShape;
  } | null>(null);
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const ready = Boolean(
    source?.format && source.content && destination?.format && destination.content,
  );

  async function generate(args: GenerateArgs = {}) {
    if (!source || !destination) return;
    setBusy(true);
    setError('');
    try {
      const options: Record<string, unknown> = {};
      if (args.fieldMappingOverrides && Object.keys(args.fieldMappingOverrides).length > 0) {
        options.field_mapping_overrides = args.fieldMappingOverrides;
      }
      if (args.reviewInferredSchema) {
        options.review_inferred_schema = true;
      }
      if (includeTests) {
        options.include_tests = true;
      }

      const generated = await generateEtl(
        {
          format: source.format,
          content: source.content,
          ...(args.confirmedSource ? { confirmed_schema: args.confirmedSource } : {}),
        },
        {
          format: destination.format,
          content: destination.content,
          ...(args.confirmedDestination ? { confirmed_schema: args.confirmedDestination } : {}),
        },
        Object.keys(options).length > 0 ? options : undefined,
      );

      if ('review' in generated) {
        setReview(generated.review);
        setResult(null);
      } else {
        setResult(generated);
        setReview(null);
      }
    } catch (err) {
      setError(err instanceof ApiError ? (err.reason ?? err.message) : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  function download() {
    if (!result) return;
    downloadBlob(new Blob([result.script], { type: 'text/x-python' }), 'etl_job.py');
  }

  function downloadTests() {
    if (!result?.tests) return;
    downloadBlob(new Blob([result.tests], { type: 'text/x-python' }), 'test_etl_job.py');
  }

  function applyMapping(destinationField: string, sourceField: string) {
    setOverrides((prev) => ({ ...prev, [destinationField]: sourceField }));
  }

  // A job belongs to the pair it was generated from: any change to either box (Clear, an edit,
  // a new file, a different format) discards it: the script panel, the finished triangle, an
  // inferred-schema review, field mappings and any error. A kept `result` also stops
  // GenerationTriangle showing the pre-generation estimate for the next pair. A result that
  // arrives after a change is still shown: it has been paid for.
  function boxChange(set: (next: DroppedSchema | null) => void) {
    return (next: DroppedSchema | null) => {
      set(next);
      setResult(null);
      setReview(null);
      setOverrides({});
      setError('');
    };
  }

  return (
    <main className="page page--wide">
      <h1>Generate an ETL script</h1>
      <p className="muted">
        Drop your source and destination below. Nothing you upload is stored — the
        generated script is the only thing returned.
      </p>

      <div className="drop-grid">
        <SchemaDropBox label="Source" value={source} onChange={boxChange(setSource)} />
        <GenerationTriangle source={source} destination={destination} result={result} busy={busy} />
        <SchemaDropBox
          label="Destination"
          value={destination}
          onChange={boxChange(setDestination)}
        />
      </div>

      <label className="field-inline">
        <input
          type="checkbox"
          checked={reviewInferredSchema}
          onChange={(e) => setReviewInferredSchema(e.target.checked)}
        />
        Review the inferred schema before generating
      </label>

      <label className="field-inline">
        <input
          type="checkbox"
          checked={includeTests}
          onChange={(e) => setIncludeTests(e.target.checked)}
        />
        Also generate a test suite
      </label>

      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <button
        className="btn btn--primary"
        disabled={!ready || busy}
        onClick={() => void generate({ reviewInferredSchema })}
      >
        {busy ? 'Generating…' : 'Generate ETL script'}
      </button>

      {review && (
        <SchemaReview
          review={review}
          busy={busy}
          onConfirm={(confirmedSource, confirmedDestination) =>
            void generate({ confirmedSource, confirmedDestination })
          }
        />
      )}

      {result && (
        <div className="result">
          <p>Your ETL script is ready.</p>
          <p className="muted small">
            {result.pricing.plan === 'free'
              ? `Free tier — no charge (${result.pricing.tier} complexity, ${result.pricing.model}).`
              : `Charged ${formatPence(result.pricing.price_charged_gbp_pence)} ` +
                `(${result.pricing.tier} complexity, ${result.pricing.model}).`}
          </p>
          {validationNotes(result.validation).map((note) => (
            <p key={note.text} className={`alert alert--${note.level}`} role="status">
              {note.text}
            </p>
          ))}
          <button className="btn btn--primary" onClick={download}>
            Download script
          </button>
          {result.tests && (
            <button className="btn btn--primary" onClick={downloadTests}>
              Download tests
            </button>
          )}
          <DeliverAsPr script={result.script} tests={result.tests} />
        </div>
      )}

      {result && (
        <FieldMapping
          unresolvedFields={result.unresolved_fields}
          overrides={overrides}
          onMap={applyMapping}
          onRegenerate={() => void generate({ fieldMappingOverrides: overrides })}
          busy={busy}
        />
      )}
    </main>
  );
}
